import { useEffect, useState } from "react";
import { Plus, Pencil, Power, Upload, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { DataTable } from "../components/common/DataTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const empty = { email: "", password: "", full_name: "", mobile: "", user_code: "", role_id: "", is_admin: false, is_field_user: false };

export default function UserMaster() {
  const { has, user: currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewedPw, setViewedPw] = useState(null);
  const viewPassword = async () => {
    try {
      const { data } = await api.get(`/users/${editing.id}/password`);
      setViewedPw(data.available ? data.password : (data.message || "Not available"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to fetch password"); }
  };
  const [form, setForm] = useState(empty);
  const [refresh, setRefresh] = useState(0);
  const [roles, setRoles] = useState([]);
  const [projectOpts, setProjectOpts] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkBatch, setBulkBatch] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const downloadTemplate = () => window.open(`${API}/users/bulk/template`, "_blank");
  const onBulkFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBulkBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/users/bulk/preview", fd);
      setBulkRows(data.rows || []); setBulkBatch(data.batch_id);
      toast.success(`${data.valid} valid, ${data.invalid} invalid`);
    } catch (err) { toast.error(err.response?.data?.detail || "Preview failed"); }
    finally { setBulkBusy(false); e.target.value = ""; }
  };
  const applyBulk = async () => {
    if (!bulkBatch) { toast.error("No valid rows to create"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/users/bulk/apply", { batch_id: bulkBatch });
      toast.success(`Created ${data.created} users` + (data.failed?.length ? `, ${data.failed.length} failed` : ""));
      setBulkOpen(false); setBulkRows([]); setBulkBatch(null); setRefresh((n) => n + 1);
    } catch (err) { toast.error(err.response?.data?.detail || "Apply failed"); }
    finally { setBulkBusy(false); }
  };

  useEffect(() => {
    api.get("/roles", { params: { page_size: 500 } }).then((r) => setRoles(r.data.items || [])).catch(() => {});
    api.get("/projects", { params: { page_size: 1000 } }).then((r) => setProjectOpts(
      (r.data.items || []).map((p) => ({ value: p.id, label: p.project_name || p.project_code }))
    )).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...empty, ...row, password: "" }); setViewedPw(null); setOpen(true); };

  const save = async () => {
    if (!form.full_name.trim() || (!editing && (!form.email.trim() || form.password.length < 6))) {
      toast.error("Name, email and a password (min 6 chars) are required");
      return;
    }
    if (!form.mobile || form.mobile.replace(/\D/g, "").length < 10) {
      toast.error("A valid mobile number (min 10 digits) is required");
      return;
    }
    try {
      if (editing) {
        const payload = { full_name: form.full_name, mobile: form.mobile, user_code: form.user_code || "", role_id: form.role_id || null, is_admin: form.is_admin, is_field_user: form.is_field_user };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", { ...form, role_id: form.role_id || null });
        toast.success("User created");
      }
      setOpen(false);
      setRefresh((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/users/${row.id}/status?is_active=${!row.is_active}`);
      setRefresh((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const columns = [
    { key: "full_name", label: "Name", render: (r) => <span className="font-medium text-slate-900">{r.full_name}</span> },
    { key: "email", label: "Email", mono: true },
    { key: "user_code", label: "Code", render: (r) => r.user_code ? <span className="font-mono text-xs text-slate-700">{r.user_code}</span> : <span className="text-xs text-slate-400">—</span> },
    { key: "role_name", label: "Role" },
    { key: "assigned_projects", label: "Projects", render: (r) => {
      const ps = r.assigned_projects || [];
      if (ps.length === 0) return <span className="text-xs text-slate-400">—</span>;
      if (ps[0] === "All Projects") return <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100">All Projects</Badge>;
      const shown = ps.slice(0, 3);
      return (
        <div className="flex flex-wrap gap-1" title={ps.join(", ")} data-testid={`user-projects-${r.id}`}>
          {shown.map((p, i) => <Badge key={i} variant="outline" className="text-[10px]">{p}</Badge>)}
          {ps.length > 3 && <Badge variant="outline" className="text-[10px] text-slate-500">+{ps.length - 3}</Badge>}
        </div>
      );
    } },
    { key: "type", label: "Type", render: (r) => (
      <div className="flex gap-1">
        {r.is_admin && <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">Admin</Badge>}
        {r.is_field_user && <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">Field</Badge>}
      </div>
    ) },
    { key: "status", label: "Status", render: (r) => <Badge className={r.is_active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-200 text-slate-600"}>{r.is_active ? "Active" : "Inactive"}</Badge> },
  ];

  return (
    <div>
      <PageHeader title="User Master" subtitle="Create users, assign roles and access type" actions={
        has("USER.CREATE") && (
          <div className="flex gap-2">
            <Button data-testid="user-bulk" variant="outline" onClick={() => { setBulkRows([]); setBulkBatch(null); setBulkOpen(true); }}><Upload className="mr-1.5 h-4 w-4" /> Bulk Upload</Button>
            <Button data-testid="user-add" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700"><Plus className="mr-1.5 h-4 w-4" /> New User</Button>
          </div>
        )
      } />

      <DataTable
        testid="users-table"
        columns={columns}
        searchPlaceholder="Search by name or email..."
        refreshKey={refresh}
        filters={[{ key: "project_id", label: "Filter by Project", options: projectOpts }]}
        load={(params) => api.get("/users", { params }).then((r) => r.data)}
        rowActions={(row) => (
          <div className="flex justify-end gap-1">
            {has("USER.EDIT") && <Button data-testid={`user-edit-${row.id}`} variant="ghost" size="sm" onClick={() => openEdit(row)}><Pencil className="h-4 w-4" /></Button>}
            {has("USER.DEACTIVATE") && <Button data-testid={`user-toggle-${row.id}`} variant="ghost" size="sm" onClick={() => toggle(row)}><Power className={`h-4 w-4 ${row.is_active ? "text-red-500" : "text-emerald-500"}`} /></Button>}
          </div>
        )}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Full Name *</Label><Input data-testid="user-name-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1" /></div>
              <div><Label>Mobile *</Label><Input data-testid="user-mobile-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="mt-1" placeholder="98XXXXXXXX" /></div>
              <div><Label>Field User Code</Label><Input data-testid="user-code-input" value={form.user_code || ""} onChange={(e) => setForm({ ...form, user_code: e.target.value })} className="mt-1" placeholder="e.g. RCPL1890" /><div className="mt-1 text-[11px] text-slate-500">Used to match field users in Opening Status Migration</div></div>
              <div><Label>Email *</Label><Input data-testid="user-email-input" type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
              <div><Label>{editing ? "New Password" : "Password *"}</Label><Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "leave blank to keep" : ""} className="mt-1" />
                {editing && currentUser?.is_admin && (
                  <div className="mt-1">
                    <button type="button" className="text-xs text-blue-600 hover:underline" onClick={viewPassword} data-testid="view-password-btn">View current password</button>
                    {viewedPw !== null && <div className="mt-1 rounded bg-slate-50 px-2 py-1 font-mono text-xs text-slate-700" data-testid="viewed-password">{viewedPw}</div>}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Role</Label>
              <SearchableSelect testid="user-role-select" value={form.role_id || ""} onChange={(v) => setForm({ ...form, role_id: v })}
                options={roles.map((r) => ({ value: r.id, label: `${r.role_code} — ${r.role_name}${r.description ? ` · ${r.description}` : ""}` }))} placeholder="Select role" className="mt-1" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <div><div className="text-sm font-medium text-slate-800">Global Admin</div><div className="text-xs text-slate-500">Full access, bypasses project/store scope</div></div>
              <Switch data-testid="user-admin-switch" checked={form.is_admin} onCheckedChange={(v) => setForm({ ...form, is_admin: v })} />
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <div><div className="text-sm font-medium text-slate-800">Field User</div><div className="text-xs text-slate-500">Restricted mobile technician</div></div>
              <Switch data-testid="user-field-switch" checked={form.is_field_user} onCheckedChange={(v) => setForm({ ...form, is_field_user: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="user-save" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-display">Bulk Create Users</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Columns: Full Name, Code, Email, Mobile, Role, Is Field User, Password. Mobile is mandatory & unique; Code is optional but must be unique.</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="bulk-template"><Download className="mr-1.5 h-4 w-4" />Template</Button>
              <label className="inline-flex cursor-pointer items-center rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50">
                <Upload className="mr-1.5 h-4 w-4" />Choose Excel
                <input type="file" accept=".xlsx" className="hidden" data-testid="bulk-file" onChange={onBulkFile} />
              </label>
              {bulkBusy && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            </div>
            {bulkRows.length > 0 && (
              <div className="max-h-72 overflow-auto rounded-md border border-slate-200">
                <table className="w-full text-sm" data-testid="bulk-preview-table">
                  <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500"><tr><th className="px-2 py-1">Row</th><th className="px-2 py-1">Name</th><th className="px-2 py-1">Code</th><th className="px-2 py-1">Email</th><th className="px-2 py-1">Mobile</th><th className="px-2 py-1">Status</th></tr></thead>
                  <tbody>{bulkRows.map((r) => (
                    <tr key={r.row} className="border-t border-slate-100">
                      <td className="px-2 py-1">{r.row}</td><td className="px-2 py-1">{r.full_name}</td><td className="px-2 py-1">{r.code || "—"}</td><td className="px-2 py-1">{r.email}</td><td className="px-2 py-1">{r.mobile}</td>
                      <td className="px-2 py-1">{r.status === "valid" ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">valid</Badge> : <span className="text-xs text-red-600" title={r.error}>{r.error}</span>}</td>
                    </tr>))}</tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button data-testid="bulk-apply" disabled={!bulkBatch || bulkBusy} onClick={applyBulk} className="bg-blue-600 hover:bg-blue-700">Create Valid Users</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
